import { TransportError } from "./types";

/**
 * Session API for the shell (spec-web-ui §4, W1 routes): sign-in / sign-out /
 * me / change-password over `/api/auth/*`, same-origin with the HttpOnly
 * cookie. Deliberately OUTSIDE StorageTransport — auth is the shell's
 * concern (which credential exists), not the components' (what data to
 * show); components stay portable to hosts with no session at all
 * (SketchUp dialog, MCP widget).
 */

export interface AuthUser {
  email: string;
  name: string;
  scope: "read" | "read_write";
}

export interface AuthApiOptions {
  /** Server origin — default "" (same-origin). Test seam like rest.ts. */
  baseUrl?: string;
  /** TEST SEAM: extra headers (cookie/origin) for non-browser callers. */
  headers?: Record<string, string>;
}

export class AuthApi {
  private readonly baseUrl: string;
  private readonly headers: Record<string, string>;

  constructor(options: AuthApiOptions = {}) {
    this.baseUrl = (options.baseUrl ?? "").replace(/\/+$/, "");
    this.headers = options.headers ?? {};
  }

  /** Boot-time session probe: user when signed in, null on the uniform 401. */
  async me(): Promise<AuthUser | null> {
    const res = await this.fetch("GET", "/api/auth/me");
    if (res.status === 401) return null;
    if (!res.ok) throw await this.error(res);
    return ((await res.json()) as { user: AuthUser }).user;
  }

  /** Throws TransportError `invalid_credentials` / `rate_limited` / … on refusal. */
  async signIn(email: string, password: string): Promise<AuthUser> {
    const res = await this.fetch("POST", "/api/auth/sign-in", { email, password });
    if (!res.ok) throw await this.error(res);
    return ((await res.json()) as { user: AuthUser }).user;
  }

  /** Idempotent server-side; the cookie dies either way. */
  async signOut(): Promise<void> {
    const res = await this.fetch("POST", "/api/auth/sign-out");
    if (!res.ok && res.status !== 401) throw await this.error(res);
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const res = await this.fetch("POST", "/api/auth/change-password", {
      currentPassword,
      newPassword,
    });
    if (!res.ok) throw await this.error(res);
  }

  private async fetch(method: "GET" | "POST", path: string, body?: unknown): Promise<Response> {
    try {
      return await fetch(`${this.baseUrl}${path}`, {
        method,
        credentials: "include",
        headers:
          body === undefined
            ? this.headers
            : { ...this.headers, "content-type": "application/json" },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch (err) {
      throw new TransportError(
        "network_error",
        err instanceof Error ? err.message : "request failed",
      );
    }
  }

  private async error(res: Response): Promise<TransportError> {
    type ErrorEnvelope = { error?: { code?: string; message?: string } };
    let envelope: ErrorEnvelope | null = null;
    try {
      envelope = (await res.json()) as ErrorEnvelope;
    } catch {
      envelope = null;
    }
    return new TransportError(
      envelope?.error?.code ?? "internal_error",
      envelope?.error?.message ?? `HTTP ${res.status}`,
      res.status,
    );
  }
}
